import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { compressImage } from '../utils/imageUtils';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';
import { listCache } from '../utils/listCache';
import { useLanguage } from '../context/LanguageContext';
import {
  MdLocationOn, MdCalendarToday, MdGroups, MdDelete,
  MdChevronLeft, MdChevronRight, MdClose, MdNotes, MdBusiness,
  MdChecklist, MdCameraAlt, MdSearch, MdAdd,
  MdCheckCircle, MdShare, MdContentCopy, MdAccessTime,
} from 'react-icons/md';

const LocationIcon  = MdLocationOn as any;
const CalendarIcon  = MdCalendarToday as any;
const GroupsIcon    = MdGroups as any;
const DeleteIcon    = MdDelete as any;
const ChevronLeft   = MdChevronLeft as any;
const ChevronRight  = MdChevronRight as any;
const CloseIcon     = MdClose as any;
const NotesIcon     = MdNotes as any;
const BusinessIcon  = MdBusiness as any;
const ChecklistIcon = MdChecklist as any;
const CameraIcon    = MdCameraAlt as any;
const SearchIcon    = MdSearch as any;
const AddIcon       = MdAdd as any;
const CheckCircleIcon = MdCheckCircle as any;
const ShareIcon     = MdShare as any;
const CopyIcon      = MdContentCopy as any;
const ClockIcon     = MdAccessTime as any;

interface ChecklistImage {
  id: number;
  file_path: string;
  note: string | null;
}

interface ActivityChecklist {
  id: number;
  survey_activity_id: number;
  survey_checklist_id: number;
  images: ChecklistImage[];
  value: string | null;
  group_name: string | null;
  description: string | null;
  input_type: string;
  options: string[] | null;
  created_at: string;
}

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

// ── ChecklistValueInput ────────────────────────────────────────────────────────
// Renders the appropriate input widget based on input_type from master checklist.
// Manages its own local UI state (radio selection, checkbox state, etc.).
// Calls onChange on every value change; debounce is handled by the parent.

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  color: '#374151',
  fontFamily: 'inherit',
  backgroundColor: '#fff',
};

interface ChecklistValueInputProps {
  item: ActivityChecklist;
  onChange: (value: string) => void;
  placeholder: string;
  otherLabel: string;
  otherPlaceholder: string;
  selectPlaceholder: string;
}

const ChecklistValueInput: React.FC<ChecklistValueInputProps> = ({
  item, onChange, placeholder, otherLabel, otherPlaceholder, selectPlaceholder,
}) => {
  const options = Array.isArray(item.options) ? item.options : [];
  const inputType = item.input_type || 'text';
  const stored = item.value || '';

  // ── SELECT state ──
  const selInitOther = inputType === 'select' && stored !== '' && !options.includes(stored);
  const [selVal, setSelVal] = useState(selInitOther ? '__other__' : stored);
  const [selOther, setSelOther] = useState(selInitOther ? stored : '');

  // ── RADIO state ──
  const radioInitOther = inputType === 'radio' && stored !== '' && !options.includes(stored);
  const [radioSel, setRadioSel] = useState(radioInitOther ? '__other__' : stored);
  const [radioOther, setRadioOther] = useState(radioInitOther ? stored : '');

  // ── CHECKBOX state ──
  const parsedCb: string[] = (() => {
    if (inputType !== 'checkbox') return [];
    try { const a = JSON.parse(stored); return Array.isArray(a) ? a : []; }
    catch { return stored ? [stored] : []; }
  })();
  const [cbChecked, setCbChecked] = useState<Set<string>>(
    new Set(parsedCb.filter(v => options.includes(v)))
  );
  const [cbOtherOn, setCbOtherOn] = useState(parsedCb.some(v => !options.includes(v)));
  const [cbOther, setCbOther] = useState(parsedCb.find(v => !options.includes(v)) || '');

  const buildCbValue = (ch: Set<string>, isOther: boolean, oText: string) => {
    const arr = [...options.filter(o => ch.has(o))];
    if (isOther && oText.trim()) arr.push(oText.trim());
    return arr.length ? JSON.stringify(arr) : '';
  };

  // ── SELECT ──
  if (inputType === 'select') {
    return (
      <div style={{ flex: 1 }}>
        <select
          value={selVal}
          onChange={e => {
            const v = e.target.value;
            setSelVal(v);
            onChange(v === '__other__' ? selOther : v === '' ? '' : v);
          }}
          style={{ ...inputBaseStyle, cursor: 'pointer' }}
        >
          <option value="">{selectPlaceholder}</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          <option value="__other__">{otherLabel}</option>
        </select>
        {selVal === '__other__' && (
          <input
            type="text"
            value={selOther}
            onChange={e => { setSelOther(e.target.value); onChange(e.target.value); }}
            placeholder={otherPlaceholder}
            style={{ ...inputBaseStyle, marginTop: 8 }}
          />
        )}
      </div>
    );
  }

  // ── RADIO ──
  if (inputType === 'radio') {
    const allOpts = [...options, '__other__'];
    return (
      <div style={{ flex: 1 }}>
        {allOpts.map(opt => {
          const isOtherOpt = opt === '__other__';
          const label = isOtherOpt ? otherLabel : opt;
          const isSelected = radioSel === opt;
          return (
            <div key={opt}>
              <div
                onClick={() => {
                  setRadioSel(opt);
                  onChange(isOtherOpt ? radioOther : opt);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer', userSelect: 'none' }}
              >
                {/* Custom radio circle */}
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${isSelected ? '#3B82F6' : '#D1D5DB'}`,
                  backgroundColor: isSelected ? '#3B82F6' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}>
                  {isSelected && <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#fff' }} />}
                </div>
                <span style={{ fontSize: 13, color: isSelected ? '#111827' : '#374151', fontWeight: isSelected ? 600 : 400 }}>
                  {label}
                </span>
              </div>
              {isOtherOpt && isSelected && (
                <input
                  type="text"
                  value={radioOther}
                  onChange={e => { setRadioOther(e.target.value); onChange(e.target.value); }}
                  placeholder={otherPlaceholder}
                  style={{ ...inputBaseStyle, marginBottom: 8, marginLeft: 28, width: 'calc(100% - 28px)' }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── CHECKBOX ──
  if (inputType === 'checkbox') {
    const allOpts = [...options, '__other__'];
    return (
      <div style={{ flex: 1 }}>
        {allOpts.map(opt => {
          const isOtherOpt = opt === '__other__';
          const label = isOtherOpt ? otherLabel : opt;
          const isChecked = isOtherOpt ? cbOtherOn : cbChecked.has(opt);
          return (
            <div key={opt}>
              <div
                onClick={() => {
                  if (isOtherOpt) {
                    const next = !cbOtherOn;
                    setCbOtherOn(next);
                    onChange(buildCbValue(cbChecked, next, cbOther));
                  } else {
                    const next = new Set(cbChecked);
                    if (next.has(opt)) next.delete(opt); else next.add(opt);
                    setCbChecked(next);
                    onChange(buildCbValue(next, cbOtherOn, cbOther));
                  }
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer', userSelect: 'none' }}
              >
                {/* Custom checkbox square */}
                <div style={{
                  width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                  border: `2px solid ${isChecked ? '#3B82F6' : '#D1D5DB'}`,
                  backgroundColor: isChecked ? '#3B82F6' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}>
                  {isChecked && (
                    <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                      <path d="M1 3.5L4 6.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span style={{ fontSize: 13, color: isChecked ? '#111827' : '#374151', fontWeight: isChecked ? 600 : 400 }}>
                  {label}
                </span>
              </div>
              {isOtherOpt && isChecked && (
                <input
                  type="text"
                  value={cbOther}
                  onChange={e => {
                    setCbOther(e.target.value);
                    onChange(buildCbValue(cbChecked, cbOtherOn, e.target.value));
                  }}
                  placeholder={otherPlaceholder}
                  style={{ ...inputBaseStyle, marginBottom: 8, marginLeft: 28, width: 'calc(100% - 28px)' }}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── TEXT (default) ──
  return (
    <textarea
      placeholder={placeholder}
      value={item.value || ''}
      onChange={e => onChange(e.target.value)}
      style={{
        flex: 1,
        minHeight: 80,
        padding: '8px 12px',
        borderRadius: 10,
        border: '1px solid #E5E7EB',
        fontSize: 13,
        resize: 'none',
        outline: 'none',
        boxSizing: 'border-box',
        lineHeight: 1.5,
        color: '#374151',
        fontFamily: 'inherit',
      }}
    />
  );
};

// ── Main screen ────────────────────────────────────────────────────────────────

export const DetailSurveyActivity: React.FC = () => {
  const { state } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t, language } = useLanguage();
  const ts = (t as any).surveyActivity || {};

  const activityId = state?.activityId;
  const { authSlice } = hooks.useAppSelector((s: any) => s);

  const [activity, setActivity] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState<any[]>([]);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Submit & Share
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [shareExpiry, setShareExpiry] = useState<number | null>(7);
  const [sharing, setSharing] = useState(false);
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareSheetDelta, setShareSheetDelta] = useState(0);
  const [shareSheetIsClosing, setShareSheetIsClosing] = useState(false);
  const shareSheetRef = useRef<HTMLDivElement>(null);
  const shareSheetDragStartY = useRef(0);
  const shareSheetDragActive = useRef(false);

  const closeShareSheet = () => {
    const h = shareSheetRef.current?.offsetHeight ?? 500;
    setShareSheetIsClosing(true);
    setShareSheetDelta(h);
    setTimeout(() => { setShowShareSheet(false); setShareSheetDelta(0); setShareSheetIsClosing(false); }, 260);
  };

  // Survey Activity Checklists
  const [activityChecklists, setActivityChecklists] = useState<ActivityChecklist[]>([]);
  const [checklistPool, setChecklistPool] = useState<any[]>([]);
  const [checklistSearch, setChecklistSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualName, setManualName] = useState('');
  const [addingManual, setAddingManual] = useState(false);
  const [uploadingCid, setUploadingCid] = useState<number | null>(null);
  // Lightbox foto checklist — scoped ke foto-foto milik 1 item checklist (bukan semua foto di
  // survey), supaya prev/next hanya navigasi antar foto item yang sedang dibuka.
  const [checklistLightbox, setChecklistLightbox] = useState<{ images: ChecklistImage[]; index: number } | null>(null);
  const valueTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const imageNoteTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const canDelete = !!activity && (
    String(authSlice.userId) === String(activity.user_id) ||
    authSlice.permissions?.includes('activity:view_all')
  );
  const [deleting, setDeleting] = useState(false);

  // Swipe carousel
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const isHorizontalRef = useRef<boolean | null>(null);
  const swipedRef = useRef(false);

  useEffect(() => {
    if (!activityId) return;
    const fetch = async () => {
      try {
        const res = await axiosInstance.get(`/survey-activities/${activityId}`);
        setActivity(res.data);
        setImages(res.data.images || []);
        if (res.data.public_token) setPublicToken(res.data.public_token);
        if (res.data.token_expires_at) setTokenExpiresAt(res.data.token_expires_at);
      } catch (err) {
        console.error('Failed to fetch survey activity:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [activityId]);

  useEffect(() => {
    if (!activityId) return;
    axiosInstance.get(`/survey-activities/${activityId}/checklists`)
      .then(r => setActivityChecklists(r.data))
      .catch(() => {});
    axiosInstance.get('/survey-checklists?is_active=true')
      .then(r => setChecklistPool(r.data))
      .catch(() => {});
  }, [activityId]);

  const handleAddChecklist = async (checklist: any) => {
    try {
      const res = await axiosInstance.post(`/survey-activities/${activityId}/checklists`, {
        survey_checklist_id: checklist.id,
      });
      setActivityChecklists(prev => [...prev, res.data]);
      setChecklistSearch('');
    } catch (err) {
      console.error('Failed to add checklist:', err);
    }
  };

  const handleAddManualChecklist = async () => {
    const name = manualName.trim();
    if (!name) return;
    setAddingManual(true);
    try {
      const res = await axiosInstance.post(`/survey-activities/${activityId}/checklists`, {
        custom_group_name: name,
      });
      setActivityChecklists(prev => [...prev, res.data]);
      setManualName('');
      setShowManualInput(false);
    } catch (err) {
      console.error('Failed to add manual checklist:', err);
    } finally {
      setAddingManual(false);
    }
  };

  const handleDeleteChecklist = async (cid: number) => {
    try {
      await axiosInstance.delete(`/survey-activities/${activityId}/checklists/${cid}`);
      setActivityChecklists(prev => prev.filter(c => c.id !== cid));
    } catch (err) {
      console.error('Failed to delete checklist:', err);
    }
  };

  const handleValueChange = (cid: number, newValue: string) => {
    setActivityChecklists(prev => prev.map(c => c.id === cid ? { ...c, value: newValue } : c));
    if (valueTimers.current[cid]) clearTimeout(valueTimers.current[cid]);
    valueTimers.current[cid] = setTimeout(async () => {
      try {
        await axiosInstance.put(`/survey-activities/${activityId}/checklists/${cid}`, { value: newValue });
      } catch (err) {
        console.error('Failed to save value:', err);
      }
    }, 800);
  };

  const handleChecklistImagesUpload = async (cid: number, files: FileList) => {
    setUploadingCid(cid);
    try {
      const compressed = await Promise.all(
        Array.from(files).map(f => compressImage(f).catch(() => f))
      );
      const fd = new FormData();
      compressed.forEach(f => fd.append('images', f));
      const res = await axiosInstance.put(`/survey-activities/${activityId}/checklists/${cid}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setActivityChecklists(prev => prev.map(c => c.id === cid ? { ...c, images: res.data.images } : c));
    } catch (err) {
      console.error('Failed to upload images:', err);
    } finally {
      setUploadingCid(null);
    }
  };

  const handleChecklistImageDelete = async (cid: number, imageId: number) => {
    try {
      await axiosInstance.delete(`/survey-activities/${activityId}/checklists/${cid}/images/${imageId}`);
      setActivityChecklists(prev => prev.map(c =>
        c.id === cid ? { ...c, images: c.images.filter(img => img.id !== imageId) } : c
      ));
    } catch (err) {
      console.error('Failed to delete checklist image:', err);
    }
  };

  // Deskripsi sekarang per-foto (bukan 1 value yang dipakai bersama 1 checklist item) — debounce
  // sama seperti handleValueChange, tapi di-key per image id.
  const handleChecklistImageNoteChange = (cid: number, imageId: number, note: string) => {
    setActivityChecklists(prev => prev.map(c =>
      c.id === cid ? { ...c, images: c.images.map(img => img.id === imageId ? { ...img, note } : img) } : c
    ));
    if (imageNoteTimers.current[imageId]) clearTimeout(imageNoteTimers.current[imageId]);
    imageNoteTimers.current[imageId] = setTimeout(async () => {
      try {
        await axiosInstance.put(`/survey-activities/${activityId}/checklists/${cid}/images/${imageId}`, { note });
      } catch (err) {
        console.error('Failed to save photo note:', err);
      }
    }, 800);
  };

  const addedChecklistIds = new Set(activityChecklists.map(c => c.survey_checklist_id));
  const filteredPool = (searchFocused || checklistSearch.trim().length >= 1)
    ? checklistPool
        .filter(c => !addedChecklistIds.has(c.id))
        .filter(c => {
          if (!checklistSearch.trim()) return true;
          const q = checklistSearch.toLowerCase();
          return (c.group_name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q);
        })
    : [];

  const formatDate = (dateStr: string) =>
    new Date(dateStr.slice(0, 10) + 'T00:00:00').toLocaleDateString(
      language === 'id' ? 'id-ID' : 'en-US',
      { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
    );

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/survey-activities/${activityId}`);
      const cache = listCache.restore('survey-activities');
      if (cache) {
        listCache.save('survey-activities', {
          ...cache,
          items: cache.items.filter((item: any) => item.id !== activityId),
        });
      }
      navigate(-1);
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // listCache nyimpan snapshot list SAAT user klik card (sebelum submit/share) — kalau tidak
  // ikut di-update di sini, status di card list akan tetap lama saat user kembali dari detail,
  // karena list tidak di-refetch ulang dari API, cuma di-restore dari cache. Pola sama seperti
  // handleDelete di atas.
  const patchListCacheStatus = (patch: Record<string, any>) => {
    const cache = listCache.restore('survey-activities');
    if (cache) {
      listCache.save('survey-activities', {
        ...cache,
        items: cache.items.map((item: any) => item.id === activityId ? { ...item, ...patch } : item),
      });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axiosInstance.post(`/survey-activities/${activityId}/submit`);
      setActivity((prev: any) => ({ ...prev, status: res.data.status, submitted_at: res.data.submitted_at }));
      patchListCacheStatus({ status: res.data.status });
    } catch (err) {
      console.error('Failed to submit survey:', err);
    } finally {
      setSubmitting(false);
      setShowSubmitConfirm(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await axiosInstance.post(`/survey-activities/${activityId}/share`, {
        expires_in_days: shareExpiry,
      });
      setPublicToken(res.data.token);
      setTokenExpiresAt(res.data.expires_at || null);
      setActivity((prev: any) => ({ ...prev, status: res.data.status }));
      patchListCacheStatus({ status: res.data.status });
    } catch (err) {
      console.error('Failed to share survey:', err);
    } finally {
      setSharing(false);
    }
  };

  const publicLink = publicToken
    ? `${window.location.origin}/survey/${publicToken}`
    : null;

  const handleCopyLink = () => {
    if (!publicLink) return;
    navigator.clipboard.writeText(publicLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const handleDeleteImage = async (imageId: number) => {
    try {
      await axiosInstance.delete(`/survey-activities/${activityId}/images/${imageId}`);
      setImages(prev => {
        const next = prev.filter(img => img.id !== imageId);
        if (currentImageIdx >= next.length) setCurrentImageIdx(Math.max(0, next.length - 1));
        return next;
      });
    } catch (err) {
      console.error('Failed to delete image:', err);
    }
  };

  // Carousel touch
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    isHorizontalRef.current = null;
    swipedRef.current = false;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchStartXRef.current;
    const dy = e.touches[0].clientY - touchStartYRef.current;
    if (isHorizontalRef.current === null) {
      isHorizontalRef.current = Math.abs(dx) > Math.abs(dy);
    }
    if (isHorizontalRef.current) e.preventDefault();
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (swipedRef.current || !isHorizontalRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartXRef.current;
    if (Math.abs(dx) < 30) return;
    swipedRef.current = true;
    if (dx < 0 && currentImageIdx < images.length - 1) setCurrentImageIdx(i => i + 1);
    if (dx > 0 && currentImageIdx > 0) setCurrentImageIdx(i => i - 1);
  };

  if (loading) {
    return (
      <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
        <components.Header title={ts.detail || 'Survey Detail'} goBack={true} />
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}>
          <text.T16 style={{ color: '#9CA3AF' }}>{t.common?.loading || 'Loading...'}</text.T16>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
        <components.Header title={ts.detail || 'Survey Detail'} goBack={true} />
        <div style={{ textAlign: 'center', padding: '80px 24px' }}>
          <text.T16 style={{ color: '#9CA3AF' }}>{ts.notFound || 'Survey activity not found.'}</text.T16>
        </div>
      </div>
    );
  }

  const currentImg = images[currentImageIdx];
  const imgUrl = currentImg ? `${IMAGE_BASE_URL}${currentImg.file_path}` : null;
  const isLocked = activity?.status === 'submitted';

  const statusBadge = (() => {
    const s = activity?.status;
    if (s === 'submitted') return { label: ts.statusSubmitted || 'Submitted', bg: '#D1FAE5', color: '#065F46' };
    if (s === 'active') return { label: ts.statusActive || 'Link Shared', bg: '#DBEAFE', color: '#1E40AF' };
    return { label: ts.statusDraft || 'Draft', bg: '#F3F4F6', color: '#6B7280' };
  })();

  const formatDateTime = (dt: string) =>
    new Date(dt).toLocaleString(language === 'id' ? 'id-ID' : 'en-US', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh', paddingBottom: 40 }}>
      <components.Header title={ts.detail || 'Survey Detail'} goBack={true} trash={canDelete} onTrash={() => setShowDeleteConfirm(true)} />

      <div style={{ marginTop: 62 }}>
        {/* Image carousel */}
        {images.length > 0 && (
          <div style={{ position: 'relative', backgroundColor: '#111', height: 240 }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {imgUrl && (
              <img
                src={imgUrl}
                alt=""
                onClick={() => { setLightboxIdx(currentImageIdx); setLightboxOpen(true); }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer', opacity: 0.92 }}
              />
            )}
            {images.length > 1 && (
              <>
                {currentImageIdx > 0 && (
                  <div onClick={() => setCurrentImageIdx(i => i - 1)} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <ChevronLeft size={22} color="#fff" />
                  </div>
                )}
                {currentImageIdx < images.length - 1 && (
                  <div onClick={() => setCurrentImageIdx(i => i + 1)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <ChevronRight size={22} color="#fff" />
                  </div>
                )}
                {/* Dots */}
                <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6 }}>
                  {images.map((_, i) => (
                    <div key={i} onClick={() => setCurrentImageIdx(i)} style={{ width: i === currentImageIdx ? 18 : 6, height: 6, borderRadius: 3, backgroundColor: i === currentImageIdx ? '#fff' : 'rgba(255,255,255,0.45)', cursor: 'pointer', transition: 'width 0.2s' }} />
                  ))}
                </div>
                {/* Delete image button */}
                {currentImg && (
                  <div onClick={() => handleDeleteImage(currentImg.id)} style={{ position: 'absolute', top: 10, right: 10, backgroundColor: 'rgba(239,68,68,0.85)', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <DeleteIcon size={16} color="#fff" />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="container" style={{ padding: '20px 16px' }}>
          {/* Header info */}
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #E5E7EB' }}>
            {/* Title row + status badge */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, gap: 10 }}>
              <text.H4 style={{ color: theme.colors.mainDark, textTransform: 'capitalize', flex: 1 }}>
                {activity.location_name || ts.noLocationName || 'Survey Activity'}
              </text.H4>
              <span style={{
                padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                backgroundColor: statusBadge.bg, color: statusBadge.color, whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {statusBadge.label}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CalendarIcon size={18} color="#3B82F6" />
                <text.T14 style={{ color: '#374151' }}>{formatDate(activity.survey_date)}</text.T14>
              </div>
              {activity.address && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <LocationIcon size={18} color="#3B82F6" style={{ marginTop: 2, flexShrink: 0 }} />
                  <text.T14 style={{ color: '#374151', lineHeight: 1.5 }}>{activity.address}</text.T14>
                </div>
              )}
              {activity.respondent_count > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <GroupsIcon size={18} color="#3B82F6" />
                  <text.T14 style={{ color: '#374151' }}>
                    {activity.respondent_count} {ts.respondents || 'respondents'}
                  </text.T14>
                </div>
              )}
              {activity.user_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <BusinessIcon size={18} color="#3B82F6" />
                  <text.T14 style={{ color: '#374151', textTransform: 'capitalize' }}>{activity.user_name}</text.T14>
                </div>
              )}
            </div>

            {/* Submitted info (anonymous) */}
            {isLocked && activity.is_anonymous_submission && (
              <div style={{ marginTop: 14, padding: '12px 14px', backgroundColor: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0' }}>
                <text.T14 style={{ fontWeight: 700, color: '#065F46', marginBottom: 6 }}>
                  {ts.submittedAnonymous || 'Submitted anonymously'}
                </text.T14>
                {activity.respondent_name && (
                  <text.T14 style={{ color: '#374151' }}>{ts.submittedBy || 'Submitted by'}: <b>{activity.respondent_name}</b></text.T14>
                )}
                {activity.respondent_contact && (
                  <text.T14 style={{ color: '#374151' }}>{activity.respondent_contact}</text.T14>
                )}
                {activity.submitted_at && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <ClockIcon size={14} color="#6B7280" />
                    <text.T14 style={{ color: '#6B7280' }}>{formatDateTime(activity.submitted_at)}</text.T14>
                  </div>
                )}
                {activity.submitted_location && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <LocationIcon size={14} color="#6B7280" />
                    <text.T14 style={{ color: '#6B7280' }}>{activity.submitted_location}</text.T14>
                  </div>
                )}
              </div>
            )}

            {/* Submitted info (direct by user) */}
            {isLocked && !activity.is_anonymous_submission && activity.submitted_at && (
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircleIcon size={16} color="#10B981" />
                <text.T14 style={{ color: '#6B7280' }}>
                  {ts.submittedAt || 'Submitted at'}: {formatDateTime(activity.submitted_at)}
                </text.T14>
              </div>
            )}

            {/* Action buttons */}
            {!isLocked && (
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button
                  onClick={() => setShowSubmitConfirm(true)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', borderRadius: 10, border: 'none', backgroundColor: '#10B981', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                >
                  <CheckCircleIcon size={16} color="#fff" />
                  {ts.submitBtn || 'Submit Survey'}
                </button>
                <button
                  onClick={() => setShowShareSheet(true)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', borderRadius: 10, border: '1px solid #3B82F6', backgroundColor: '#fff', color: '#3B82F6', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                >
                  <ShareIcon size={16} color="#3B82F6" />
                  {ts.shareBtn || 'Share Link'}
                </button>
              </div>
            )}
          </div>

          {/* Findings */}
          {activity.findings && (
            <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <NotesIcon size={18} color="#3B82F6" />
                <text.H5 style={{ color: theme.colors.mainDark }}>{ts.findings || 'Survey Findings'}</text.H5>
              </div>
              <text.T14 style={{ color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', textTransform: 'capitalize' }}>
                {activity.findings}
              </text.T14>
            </div>
          )}

          {/* Notes */}
          {activity.notes && (
            <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <NotesIcon size={18} color="#9CA3AF" />
                <text.H5 style={{ color: theme.colors.mainDark }}>{ts.notes || 'Notes'}</text.H5>
              </div>
              <text.T14 style={{ color: '#4B5563', lineHeight: 1.7, whiteSpace: 'pre-wrap', textTransform: 'capitalize' }}>
                {activity.notes}
              </text.T14>
            </div>
          )}

          {/* Survey Checklist section */}
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #E5E7EB' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <ChecklistIcon size={18} color="#3B82F6" />
              <text.H5 style={{ color: theme.colors.mainDark }}>{ts.checklistSection || 'Survey Checklist'}</text.H5>
            </div>

            {/* Search box — hidden when submitted */}
            {!isLocked && <div style={{ position: 'relative', marginBottom: 14 }}>
              <div style={{ position: 'relative' }}>
                <SearchIcon size={16} color="#9CA3AF" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder={ts.checklistSearch || 'Cari checklist...'}
                  value={checklistSearch}
                  onChange={e => setChecklistSearch(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  style={{ width: '100%', padding: '10px 14px 10px 36px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#374151' }}
                />
                {checklistSearch.length > 0 && (
                  <div onClick={() => setChecklistSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', padding: 4 }}>
                    <CloseIcon size={14} color="#9CA3AF" />
                  </div>
                )}
              </div>

              {/* Dropdown results */}
              {filteredPool.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: 220, overflowY: 'auto', marginTop: 4 }}>
                  {filteredPool.map((c: any) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ flex: 1, marginRight: 10 }}>
                        {c.group_name && <text.T14 style={{ fontWeight: 700, color: '#111827' }}>{c.group_name}</text.T14>}
                        {c.description && <text.T14 style={{ color: '#6B7280', marginTop: 2 }}>{c.description}</text.T14>}
                      </div>
                      <button
                        onClick={() => handleAddChecklist(c)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: 'none', backgroundColor: '#3B82F6', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                      >
                        <AddIcon size={14} color="#fff" />
                        {ts.checklistAdd || 'Tambah'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {checklistSearch.trim().length >= 1 && filteredPool.length === 0 && searchFocused && (
                <text.T14 style={{ color: '#9CA3AF', marginTop: 8, display: 'block' }}>
                  {ts.checklistNoResult || 'Tidak ada checklist yang cocok.'}
                </text.T14>
              )}
            </div>}

            {/* Manual add — item checklist ad-hoc, khusus survey ini (tidak masuk master list) */}
            {!isLocked && (
              showManualInput ? (
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <input
                    type="text"
                    autoFocus
                    placeholder={ts.checklistManualPlaceholder || 'Nama item checklist...'}
                    value={manualName}
                    onChange={e => setManualName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddManualChecklist(); }}
                    // minWidth 0: input punya lebar minimum bawaan browser (~20 karakter) — tanpa ini
                    // input tidak menyusut & tombol Batal terdorong keluar container di layar kecil
                    style={{ flex: 1, minWidth: 0, padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#374151' }}
                  />
                  <button
                    onClick={handleAddManualChecklist}
                    disabled={addingManual || !manualName.trim()}
                    style={{ padding: '0 16px', borderRadius: 10, border: 'none', backgroundColor: '#3B82F6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: addingManual ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                  >
                    {addingManual ? '...' : (ts.checklistAdd || 'Tambah')}
                  </button>
                  <button
                    onClick={() => { setShowManualInput(false); setManualName(''); }}
                    style={{ padding: '0 14px', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#fff', color: '#6B7280', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}
                  >
                    {t.common?.cancel || 'Batal'}
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => setShowManualInput(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, cursor: 'pointer', width: 'fit-content' }}
                >
                  <AddIcon size={14} color="#3B82F6" />
                  <text.T14 style={{ color: '#3B82F6', fontWeight: 600 }}>
                    {ts.checklistAddManual || 'Tambah Manual'}
                  </text.T14>
                </div>
              )
            )}

            {/* Added checklist items */}
            {activityChecklists.length === 0 ? (
              <text.T14 style={{ color: '#9CA3AF' }}>{ts.checklistEmpty || 'Belum ada checklist yang ditambahkan.'}</text.T14>
            ) : (
              activityChecklists.map(item => (
                <div key={item.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 14, marginBottom: 10 }}>
                  {/* Row: group + description + delete */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ flex: 1, marginRight: 8 }}>
                      {item.group_name && (
                        <text.T14 style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                          {item.group_name}
                        </text.T14>
                      )}
                      <text.T14 style={{ fontWeight: 700, color: '#111827', marginTop: item.group_name ? 2 : 0 }}>
                        {item.description || item.group_name}
                      </text.T14>
                    </div>
                    {!isLocked && (
                      <div
                        onClick={() => handleDeleteChecklist(item.id)}
                        style={{ padding: 4, cursor: 'pointer', flexShrink: 0 }}
                      >
                        <DeleteIcon size={18} color="#EF4444" />
                      </div>
                    )}
                  </div>

                  {/* Foto — tiap foto punya keterangan sendiri (bukan 1 value utk seluruh item) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {item.images.map(img => (
                      <div key={img.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ position: 'relative', width: 60, height: 60, flexShrink: 0 }}>
                          <img
                            src={`${IMAGE_BASE_URL}${img.file_path}`}
                            alt=""
                            onClick={() => {
                              const idx = item.images.findIndex(i => i.id === img.id);
                              setChecklistLightbox({ images: item.images, index: idx >= 0 ? idx : 0 });
                            }}
                            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', display: 'block' }}
                          />
                          {!isLocked && (
                            <div
                              onClick={() => handleChecklistImageDelete(item.id, img.id)}
                              style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: '50%', backgroundColor: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                              <CloseIcon size={10} color="#fff" />
                            </div>
                          )}
                        </div>
                        {isLocked ? (
                          <div style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', fontSize: 13, color: img.note ? '#374151' : '#9CA3AF', minHeight: 60, lineHeight: 1.5, whiteSpace: 'pre-wrap', boxSizing: 'border-box' }}>
                            {img.note || '—'}
                          </div>
                        ) : (
                          <textarea
                            placeholder={ts.checklistPhotoNote || 'Keterangan foto...'}
                            value={img.note || ''}
                            onChange={e => handleChecklistImageNoteChange(item.id, img.id, e.target.value)}
                            style={{
                              flex: 1, minHeight: 60, padding: '8px 10px', borderRadius: 8,
                              border: '1px solid #E5E7EB', fontSize: 13, resize: 'none', outline: 'none',
                              boxSizing: 'border-box', color: '#374151', fontFamily: 'inherit', lineHeight: 1.5,
                            }}
                          />
                        )}
                      </div>
                    ))}
                    {!isLocked && (
                      <label style={{ width: 60, height: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, borderRadius: 8, border: '1px dashed #D1D5DB', cursor: 'pointer' }}>
                        {uploadingCid === item.id ? (
                          <text.T14 style={{ color: '#3B82F6', fontSize: 10 }}>...</text.T14>
                        ) : (
                          <>
                            <CameraIcon size={16} color="#9CA3AF" />
                            <text.T14 style={{ color: '#9CA3AF', fontSize: 9, textAlign: 'center', lineHeight: 1.1 }}>
                              {ts.checklistUploadPhoto || 'Upload Foto'}
                            </text.T14>
                          </>
                        )}
                        <input type="file" multiple style={{ display: 'none' }} accept="image/*" onChange={e => {
                          if (e.target.files && e.target.files.length > 0) handleChecklistImagesUpload(item.id, e.target.files);
                          e.target.value = '';
                        }} />
                      </label>
                    )}

                    {/* Jawaban terstruktur (select/radio/checkbox) tetap di level item — beda
                        konsepnya dari keterangan foto di atas. Tipe text/default tidak lagi
                        punya field sendiri, sudah digantikan keterangan per-foto; fallback ke
                        value lama hanya utk data lama yang belum punya foto sama sekali. */}
                    {['select', 'radio', 'checkbox'].includes(item.input_type) ? (
                      isLocked ? (
                        (() => {
                          if (item.input_type === 'checkbox' && item.value) {
                            let parsed: string[] = [];
                            try { parsed = JSON.parse(item.value); } catch { parsed = [item.value]; }
                            return parsed.length > 0 ? (
                              <ul style={{ flex: 1, margin: 0, paddingLeft: 18, listStyleType: 'disc' }}>
                                {parsed.map((v, i) => (
                                  <li key={i} style={{ fontSize: 13, color: '#374151', lineHeight: 1.8 }}>{v}</li>
                                ))}
                              </ul>
                            ) : (
                              <div style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', fontSize: 13, color: '#9CA3AF', minHeight: 40 }}>—</div>
                            );
                          }
                          return (
                            <div style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', fontSize: 13, color: item.value ? '#374151' : '#9CA3AF', minHeight: 40, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {item.value || '—'}
                            </div>
                          );
                        })()
                      ) : (
                        <ChecklistValueInput
                          key={item.id}
                          item={item}
                          onChange={val => handleValueChange(item.id, val)}
                          placeholder={ts.checklistValue || ts.checklistNote || 'Nilai / Keterangan...'}
                          otherLabel={ts.checklistOther || 'Lainnya'}
                          otherPlaceholder={ts.checklistOtherPlaceholder || 'Masukkan nilai...'}
                          selectPlaceholder={ts.checklistSelectPlaceholder || '— Pilih —'}
                        />
                      )
                    ) : (
                      isLocked && item.images.length === 0 && item.value ? (
                        <div style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', fontSize: 13, color: '#374151', minHeight: 40, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {item.value}
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Image thumbnails */}
          {images.length > 0 && (
            <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, border: '1px solid #E5E7EB' }}>
              <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 12 }}>
                {ts.photos || 'Photos'} ({images.length})
              </text.H5>
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                {images.map((img, i) => (
                  <div
                    key={img.id}
                    onClick={() => { setLightboxIdx(i); setLightboxOpen(true); }}
                    style={{
                      width: 80, height: 80, flexShrink: 0, borderRadius: 10, overflow: 'hidden',
                      border: i === currentImageIdx ? '2px solid #3B82F6' : '2px solid transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <img src={`${IMAGE_BASE_URL}${img.file_path}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox — dibatasi max-width 650px sama seperti #app (lihat global.scss), supaya saat
          dibuka di layar lebar (PC) tombol close & nav tetap nempel di kolom aplikasi, bukan di
          pojok seluruh browser window. Pola sama dengan components/AttachmentViewer.tsx. */}
      {lightboxOpen && images.length > 0 && (
        <div style={{ position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 650, backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={() => setLightboxOpen(false)} style={{ position: 'absolute', top: 16, right: 16, cursor: 'pointer', padding: 8 }}>
            <CloseIcon size={28} color="#fff" />
          </div>
          <img src={`${IMAGE_BASE_URL}${images[lightboxIdx]?.file_path}`} alt="" style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} />
          {lightboxIdx > 0 && (
            <div onClick={() => setLightboxIdx(i => i - 1)} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 8 }}>
              <ChevronLeft size={28} color="#fff" />
            </div>
          )}
          {lightboxIdx < images.length - 1 && (
            <div onClick={() => setLightboxIdx(i => i + 1)} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 8 }}>
              <ChevronRight size={28} color="#fff" />
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 20, left: 0, right: 0, textAlign: 'center' }}>
            <text.T14 style={{ color: 'rgba(255,255,255,0.6)' }}>{lightboxIdx + 1} / {images.length}</text.T14>
          </div>
        </div>
      )}

      {/* Lightbox checklist — sama persis polanya, plus prev/next antar foto milik item yg sama */}
      {checklistLightbox && (
        <div onClick={() => setChecklistLightbox(null)} style={{ position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 650, backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 5500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={() => setChecklistLightbox(null)} style={{ position: 'absolute', top: 16, right: 16, cursor: 'pointer', padding: 8 }}>
            <CloseIcon size={28} color="#fff" />
          </div>
          <img
            src={`${IMAGE_BASE_URL}${checklistLightbox.images[checklistLightbox.index]?.file_path}`}
            alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }}
          />
          {checklistLightbox.index > 0 && (
            <div
              onClick={e => { e.stopPropagation(); setChecklistLightbox(lb => lb ? { ...lb, index: lb.index - 1 } : lb); }}
              style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 8 }}
            >
              <ChevronLeft size={28} color="#fff" />
            </div>
          )}
          {checklistLightbox.index < checklistLightbox.images.length - 1 && (
            <div
              onClick={e => { e.stopPropagation(); setChecklistLightbox(lb => lb ? { ...lb, index: lb.index + 1 } : lb); }}
              style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 8 }}
            >
              <ChevronRight size={28} color="#fff" />
            </div>
          )}
          {checklistLightbox.images.length > 1 && (
            <div style={{ position: 'absolute', bottom: 20, left: 0, right: 0, textAlign: 'center' }}>
              <text.T14 style={{ color: 'rgba(255,255,255,0.6)' }}>{checklistLightbox.index + 1} / {checklistLightbox.images.length}</text.T14>
            </div>
          )}
        </div>
      )}

      {/* Submit confirmation */}
      {showSubmitConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center' }}>
            <CheckCircleIcon size={48} color="#10B981" style={{ marginBottom: 12 }} />
            <text.H4 style={{ marginBottom: 8 }}>{ts.submitConfirmTitle || 'Submit Survey?'}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>
              {ts.submitConfirmDesc || 'After submission, this survey cannot be edited. Are you sure?'}
            </text.T14>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowSubmitConfirm(false)}
                style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}
              >
                {(t.common as any)?.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: 'none', backgroundColor: '#10B981', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, color: '#fff' }}
              >
                {submitting ? '...' : (ts.submitBtn || 'Submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share bottom sheet */}
      {showShareSheet && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4500 }}>
          <div onClick={closeShareSheet} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)' }} />
          <div
            ref={shareSheetRef}
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              backgroundColor: '#fff', borderRadius: '20px 20px 0 0',
              padding: '0 0 env(safe-area-inset-bottom)',
              transform: `translateY(${shareSheetDelta}px)`,
              transition: (shareSheetDelta === 0 || shareSheetIsClosing) ? 'transform 0.26s ease' : 'none',
            }}
            onTouchStart={e => {
              shareSheetDragStartY.current = e.touches[0].clientY;
              shareSheetDragActive.current = true;
            }}
            onTouchMove={e => {
              if (!shareSheetDragActive.current) return;
              const dy = e.touches[0].clientY - shareSheetDragStartY.current;
              if (dy > 0) setShareSheetDelta(dy);
            }}
            onTouchEnd={() => {
              shareSheetDragActive.current = false;
              if (shareSheetDelta > 80) {
                closeShareSheet();
              } else {
                setShareSheetDelta(0);
              }
            }}
          >
            {/* Drag handle */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4 }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#D1D5DB' }} />
            </div>

            <div style={{ padding: '12px 20px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <text.H5 style={{ color: theme.colors.mainDark }}>{ts.shareTitle || 'Share Survey'}</text.H5>
                <div onClick={closeShareSheet} style={{ cursor: 'pointer', padding: 4 }}>
                  <CloseIcon size={20} color="#9CA3AF" />
                </div>
              </div>

              {/* Expiry options */}
              <text.T14 style={{ fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                {ts.shareExpiryLabel || 'Link Expiry'}
              </text.T14>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
                {[
                  { label: ts.share1Day || '1 Day', value: 1 },
                  { label: ts.share3Days || '3 Days', value: 3 },
                  { label: ts.share7Days || '7 Days', value: 7 },
                  { label: ts.share30Days || '30 Days', value: 30 },
                  { label: ts.shareNoExpiry || 'No Expiry', value: null },
                ].map(opt => (
                  <button
                    key={String(opt.value)}
                    onClick={() => setShareExpiry(opt.value)}
                    style={{
                      padding: '10px 0', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: shareExpiry === opt.value ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                      backgroundColor: shareExpiry === opt.value ? '#EFF6FF' : '#fff',
                      color: shareExpiry === opt.value ? '#3B82F6' : '#374151',
                      gridColumn: opt.value === null ? 'span 2' : 'span 1',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Generated link */}
              {publicLink ? (
                <div style={{ marginBottom: 16 }}>
                  <text.T14 style={{ fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    {ts.shareLinkLabel || 'Public Link'}
                  </text.T14>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {publicLink}
                    </div>
                    <button
                      onClick={handleCopyLink}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '10px 14px', borderRadius: 10, border: 'none', backgroundColor: linkCopied ? '#10B981' : '#3B82F6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                    >
                      <CopyIcon size={14} color="#fff" />
                      {linkCopied ? (ts.shareCopied || 'Copied!') : (ts.shareCopy || 'Copy')}
                    </button>
                  </div>
                  {tokenExpiresAt && (
                    <text.T14 style={{ color: '#9CA3AF', marginTop: 6, fontSize: 12 }}>
                      {ts.shareExpiresAt || 'Expires'}: {formatDateTime(tokenExpiresAt)}
                    </text.T14>
                  )}
                </div>
              ) : null}

              {/* Generate / Renew button */}
              <button
                onClick={handleShare}
                disabled={sharing}
                style={{ width: '100%', padding: '13px 0', borderRadius: 12, border: 'none', backgroundColor: '#3B82F6', color: '#fff', fontSize: 15, fontWeight: 700, cursor: sharing ? 'not-allowed' : 'pointer' }}
              >
                {sharing ? '...' : (publicLink ? (ts.shareRenew || 'Renew Link') : (ts.shareGenerate || 'Generate Link'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center' }}>
            <DeleteIcon size={48} color="#EF4444" style={{ marginBottom: 12 }} />
            <text.H4 style={{ marginBottom: 8 }}>{ts.deleteTitle || 'Delete Survey?'}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>
              {ts.deleteDesc || 'This survey activity and all its photos will be permanently deleted.'}
            </text.T14>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: '1px solid #E5E7EB', backgroundColor: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: '600', color: '#374151' }}
              >
                {t.common?.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: 'none', backgroundColor: '#EF4444', cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: '600', color: '#fff' }}
              >
                {deleting ? '...' : (t.common?.delete || 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
